import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import StampCard from "@/components/StampCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Car, MapPin, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  pending: { label: "Ausstehend", color: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  confirmed: { label: "Bestätigt", color: "bg-blue-500/10 text-blue-300 border-blue-500/30" },
  completed: { label: "Abgeschlossen", color: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  cancelled: { label: "Storniert", color: "bg-red-500/10 text-red-300 border-red-500/30" },
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [b, _] = await Promise.all([api.get("/bookings/me"), refresh()]);
      setBookings(b.data);
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCancel = async (booking_id) => {
    if (!window.confirm("Buchung wirklich stornieren?")) return;
    try {
      await api.delete(`/bookings/${booking_id}`);
      toast.success("Buchung storniert");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen grain">
      <Header />
      <main className="relative max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Hallo {user.name?.split(" ")[0] || "Kunde"}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight">
            Deine <span className="text-gradient-gold">Glanz</span>-Übersicht
          </h1>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <StampCard stamps={user.stamps || 0} freeCredits={user.free_credits || 0} userName={user.name} />

          <div className="rounded-2xl border border-white/10 bg-zinc-950 p-8 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Schnellaktion</div>
              <h3 className="font-heading text-2xl mb-3">Neuen Termin buchen</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Innenraumaufbereitung vor deiner Tür. Wähle Paket, Datum und Adresse.
                {user.free_credits > 0 && (
                  <span className="block mt-3 text-primary">
                    Du hast {user.free_credits}× Gratis-Aufbereitung verfügbar.
                  </span>
                )}
              </p>
            </div>
            <Button
              data-testid="dashboard-new-booking"
              onClick={() => navigate("/booking")}
              className="mt-6 rounded-full bg-primary text-primary-foreground hover:brightness-110 self-start"
            >
              <Plus className="w-4 h-4 mr-2" /> Termin buchen
            </Button>
          </div>
        </div>

        <div className="mb-6 flex items-end justify-between">
          <h2 className="font-heading text-2xl">Deine Buchungen</h2>
          <span className="text-xs text-zinc-500">{bookings.length} insgesamt</span>
        </div>

        {loading ? (
          <div className="text-zinc-500">Lade…</div>
        ) : bookings.length === 0 ? (
          <div
            data-testid="dashboard-empty"
            className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-zinc-500"
          >
            Noch keine Buchung. Starte deine erste Aufbereitung.
          </div>
        ) : (
          <div className="grid gap-4">
            {bookings.map((b) => (
              <div
                key={b.booking_id}
                data-testid={`booking-row-${b.booking_id}`}
                className="rounded-xl border border-white/10 bg-zinc-950 p-6 grid md:grid-cols-[1fr_auto] gap-4 items-center"
              >
                <div className="grid sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Paket</div>
                    <div className="font-medium mt-1">{b.package_name}</div>
                    {b.is_free && <Badge className="mt-1 bg-primary text-primary-foreground">Gratis</Badge>}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Datum
                    </div>
                    <div className="font-medium mt-1">
                      {b.date} · {b.time}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Adresse
                    </div>
                    <div className="font-medium mt-1 truncate">{b.address}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
                      <Car className="w-3 h-3" /> Fahrzeug
                    </div>
                    <div className="font-medium mt-1 truncate">{b.vehicle}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={STATUS_LABEL[b.status]?.color || ""}>
                    {STATUS_LABEL[b.status]?.label || b.status}
                  </Badge>
                  {b.status !== "completed" && b.status !== "cancelled" && (
                    <Button
                      data-testid={`booking-cancel-${b.booking_id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => onCancel(b.booking_id)}
                      className="text-zinc-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
